import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { ObjectStateType, type SetTagWidgetData } from '@playbooks/action-cards-utils/src'

import StateManager from '../../services/stateManager'
import { UndoIcon } from '../../components/ui'
import { useTranslation } from '../../contexts/localization/Localization'
import { CustomButton } from '../../components/ui/CustomButton'
import { useAppContext } from '../../contexts/AppContext'

interface SetTagWidgetProps {
  widget: SetTagWidgetData
}

/**
 * Widget for Set Tags
 */
const SetTagWidget: FunctionComponent<SetTagWidgetProps> = ({ widget }) => {
  const { update: updateState } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const { text: t } = useTranslation()
  const widgetState = StateManager.getOrCreate(widget, ObjectStateType.IsTagSet)

  function handleButtonClick() {
    setIsLoading(true)
    try {
      // Update the Action Card state (widget value, tags)
      updateState(widgetState, 'isActive', true)
    } catch (error: unknown) {
      console.error(`Error while setting tag: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }

  function undoSetTag() {
    // Update the Action Card state (widget value, tags)
    updateState(widgetState, 'isActive', false)
  }

  return (
    <div className="flex items-center">
      <CustomButton
        size="lg"
        isLoading={isLoading}
        onClick={() => {
          if (!isLoading) handleButtonClick()
        }}
        disabled={!!widgetState?.isActive}
      >
        <>{widget.buttonLabel || t('setTagWidget_defaultButton')}</>
      </CustomButton>

      {widgetState?.isActive && (
        <CustomButton onClick={undoSetTag} type="button" className="ml-2" tooltip={t('genericAiWidget_undoTooltip')}>
          <UndoIcon className="w-4 h-4" />
        </CustomButton>
      )}
    </div>
  )
}

export default SetTagWidget
